import { Box, Tab, Tabs } from "@mui/material";
import { Link, useLocation } from "react-router";

import { useAuth } from "@/auth/authContext";
import { activeServicePage, pageHref, visibleServicePages } from "@/services/registry";

/**
 * 服務內切換面向（儀表／管理／設定）的 tab。
 *
 * **這是水平切換的唯一控制項，桌機與手機共用同一組 UI。** 那三個子頁是同一個
 * 服務的三個面向而不是階層，所以正確的控制項是 tab 而不是麵包屑 —— 麵包屑
 * 表達包含關係、只能往上，用它切換兄弟會把使用者困住（理由寫在
 * `useServicePageCrumbs.ts`）。
 *
 * 兩個軸各有自己的控制項，不重複：
 *
 *   垂直（包含）  哪一個服務    → 左側第二層面板
 *   水平（面向）  服務的哪一面  → 這裡
 *
 * 位置在頂列之下、麵包屑之上，自己佔滿一行：tab 是這個服務的次級導覽，
 * 跟麵包屑（我在哪）與頁面動作（我能做什麼）不是同一件事，混在一行會讓
 * 三種東西擠在一起。底部那條線把它與下面的頁面內容分開，也是 Material 的
 * tab 慣例。
 *
 * 資料來源與面板、路由、麵包屑同一份（服務自己的 `pages.ts` + 權限過濾），
 * 所以不會出現「桌機看得到、手機找不到」或「選單有但點進去沒權限」。
 *
 * 不適用時整列都不畫（連底線也沒有）：不在服務內、或這個服務只有一個面向。
 */
export function ServicePageTabs() {
  const { me } = useAuth();
  const location = useLocation();

  const code = location.pathname.split("/")[1];
  const inService = Boolean(code) && (me?.services ?? []).some((service) => service.code === code);
  if (!inService || !code) {
    return null;
  }

  const pages = visibleServicePages(code, me);
  if (pages.length <= 1) {
    return null;
  }

  const current = activeServicePage(code, location.pathname);

  return (
    <Box sx={{ px: 1.5, borderBottom: 1, borderColor: "divider", flex: "none" }}>
      <Tabs
        // 對不到面向時（例如更深層的詳情頁還沒宣告）不亮任何一個，
        // 而不是誤亮第一個
        value={current?.path ?? false}
        variant="scrollable"
        scrollButtons={false}
        aria-label="服務子頁"
        sx={{ minHeight: 44, "& .MuiTab-root": { minHeight: 44, py: 0 } }}
      >
        {pages.map((page) => (
          <Tab
            key={page.path || "index"}
            value={page.path}
            label={page.label}
            component={Link}
            to={pageHref(code, page.path)}
          />
        ))}
      </Tabs>
    </Box>
  );
}
